import React, { useRef, useEffect, useState } from 'react';
import { Bold, Underline, List, ListOrdered, AtSign } from 'lucide-react';
import useMentionPlugin, { extractMentionsFromHTML } from './MentionPlugin';
import VariablePicker from './VariablePicker';

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder, 
  className = '', 
  onMentionClick,
  enableMentions = true,
  enableVariables = true,
  clientId = null,
  contactId = null,
  taskId = null,  // Task ID for uploading pasted images
  onRequestTaskSave = null  // Callback to save task if it doesn't have ID yet
}) => {
  const editorRef = useRef(null);
  const [isActive, setIsActive] = useState({
    bold: false,
    underline: false,
    bulletList: false,
    numberedList: false
  });

  // Initialize mention plugin
  const {
    mentionState,
    handleMentionTrigger,
    handleInput: handleMentionInput,
    closeMentionDropdown,
    insertMention,
    MentionDropdown
  } = useMentionPlugin(editorRef, onMentionClick);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  useEffect(() => {
    const updateActiveStates = () => {
      if (!editorRef.current) return;

      setIsActive({
        bold: document.queryCommandState('bold'),
        underline: document.queryCommandState('underline'),
        bulletList: document.queryCommandState('insertUnorderedList'),
        numberedList: document.queryCommandState('insertOrderedList')
      });
    };

    const handleSelectionChange = () => {
      updateActiveStates();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      
      // Extract mentions for callback
      if (enableMentions) {
        const mentions = extractMentionsFromHTML(content);
        onChange(content, mentions);
        handleMentionInput();
      } else {
        onChange(content);
      }
    }
  };

  const handleKeyDown = (e) => {
    // Handle mention trigger
    if (enableMentions) {
      handleMentionTrigger(e);
    }

    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          executeCommand('bold');
          break;
        case 'u':
          e.preventDefault();
          executeCommand('underline');
          break;
        default:
          break;
      }
    }
  };

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    
    // Update active states after command execution
    setTimeout(() => {
      setIsActive({
        bold: document.queryCommandState('bold'),
        underline: document.queryCommandState('underline'),
        bulletList: document.queryCommandState('insertUnorderedList'),
        numberedList: document.queryCommandState('insertOrderedList')
      });
    }, 10);
    
    handleInput();
  };

  const handlePaste = async (e) => {
    const clipboardData = e.clipboardData || e.originalEvent.clipboardData;
    const items = clipboardData.items;
    
    // Check if clipboard contains images
    let hasImage = false;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        hasImage = true;
        e.preventDefault();
        
        const imageFile = items[i].getAsFile();
        await handleImagePaste(imageFile);
        return;
      }
    }
    
    // If no image, check for HTML with images
    const htmlData = clipboardData.getData('text/html');
    if (htmlData && htmlData.includes('<img')) {
      e.preventDefault();
      await handleHtmlPaste(htmlData);
      return;
    }
    
    // Default: paste as plain text with basic formatting preserved
    e.preventDefault();
    const text = clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };
  
  const handleImagePaste = async (imageFile) => {
    try {
      // Check if we have a task ID
      let currentTaskId = taskId;
      
      // If no task ID and callback provided, request task save first
      if (!currentTaskId && onRequestTaskSave) {
        const savedTask = await onRequestTaskSave();
        currentTaskId = savedTask?.id;
      }
      
      if (!currentTaskId) {
        alert('Please save the task first before pasting images.');
        return;
      }
      
      // Show loading indicator
      const loadingSpan = document.createElement('span');
      loadingSpan.textContent = '⏳ Uploading image...';
      loadingSpan.style.color = '#6B7280';
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      range.insertNode(loadingSpan);
      
      // Upload image to task attachments
      const formData = new FormData();
      formData.append('file', imageFile);
      
      const token = localStorage.getItem('access_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${BACKEND_URL}/api/tasks/${currentTaskId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload image');
      }
      
      const result = await response.json();
      const attachmentId = result.id || result.attachment_id;
      
      // Remove loading indicator
      loadingSpan.remove();
      
      // Insert image tag with attachment URL
      const img = document.createElement('img');
      img.src = `${BACKEND_URL}/api/attachments/${attachmentId}/download`;
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.alt = imageFile.name;
      
      range.insertNode(img);
      
      // Move cursor after image
      range.setStartAfter(img);
      range.setEndAfter(img);
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Trigger onChange
      handleInput();
      
    } catch (error) {
      console.error('Error uploading pasted image:', error);
      alert('Failed to upload image. Please try again.');
    }
  };
  
  const handleHtmlPaste = async (htmlData) => {
    // Parse HTML to find images
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlData;
    const images = tempDiv.querySelectorAll('img');
    
    if (images.length === 0) {
      // No images, paste HTML as is
      document.execCommand('insertHTML', false, htmlData);
      handleInput();
      return;
    }
    
    // Check if we have a task ID
    let currentTaskId = taskId;
    
    // If no task ID and callback provided, request task save first
    if (!currentTaskId && onRequestTaskSave) {
      try {
        const savedTask = await onRequestTaskSave();
        currentTaskId = savedTask?.id;
      } catch (error) {
        console.error('Failed to save task:', error);
      }
    }
    
    if (!currentTaskId) {
      alert('Please save the task first before pasting images.');
      // Paste without images
      const text = tempDiv.textContent;
      document.execCommand('insertText', false, text);
      return;
    }
    
    // Process each image
    const token = localStorage.getItem('access_token');
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    for (let img of images) {
      const src = img.src;
      
      try {
        // Handle base64 images
        if (src.startsWith('data:image/')) {
          const blob = await fetch(src).then(r => r.blob());
          const file = new File([blob], 'pasted-image.png', { type: blob.type });
          
          // Upload to attachments
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await fetch(`${BACKEND_URL}/api/tasks/${currentTaskId}/attachments`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          
          if (response.ok) {
            const result = await response.json();
            const attachmentId = result.id || result.attachment_id;
            img.src = `${BACKEND_URL}/api/attachments/${attachmentId}/download`;
          }
        }
        // Handle external URLs
        else if (src.startsWith('http://') || src.startsWith('https://')) {
          try {
            // Try to fetch and upload the external image
            const imageResponse = await fetch(src);
            const blob = await imageResponse.blob();
            const file = new File([blob], 'external-image.png', { type: blob.type });
            
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await fetch(`${BACKEND_URL}/api/tasks/${currentTaskId}/attachments`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: formData
            });
            
            if (response.ok) {
              const result = await response.json();
              const attachmentId = result.id || result.attachment_id;
              img.src = `${BACKEND_URL}/api/attachments/${attachmentId}/download`;
            }
          } catch (fetchError) {
            // If fetch fails (CORS, etc), keep original URL
            console.warn('Could not fetch external image:', src, fetchError);
          }
        }
        // CID or other references - remove the image
        else if (src.startsWith('cid:')) {
          img.remove();
        }
      } catch (error) {
        console.error('Error processing image:', error);
        // Keep the image as-is if processing fails
      }
    }
    
    // Insert the processed HTML
    document.execCommand('insertHTML', false, tempDiv.innerHTML);
    handleInput();
  };

  const handleClick = (e) => {
    // Handle mention clicks
    if (enableMentions && e.target.hasAttribute('data-mention-id')) {
      e.preventDefault();
      const userId = e.target.getAttribute('data-mention-id');
      const userName = e.target.getAttribute('data-mention-name');
      onMentionClick?.({ id: userId, name: userName });
    }
  };

  const handleVariableSelect = (variable) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    
    if (range) {
      // Insert variable at current cursor position
      const variableElement = document.createElement('span');
      variableElement.className = 'inline-block px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-mono';
      variableElement.textContent = variable;
      variableElement.contentEditable = 'false';
      
      range.deleteContents();
      range.insertNode(variableElement);
      
      // Add space after variable
      const spaceNode = document.createTextNode(' ');
      variableElement.parentNode.insertBefore(spaceNode, variableElement.nextSibling);
      
      // Move cursor after space
      const finalRange = document.createRange();
      finalRange.setStartAfter(spaceNode);
      finalRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(finalRange);
      
      handleInput();
    }
  };

  const ToolbarButton = ({ command, icon: Icon, title, isActive, value = null }) => (
    <button
      type="button"
      onClick={() => executeCommand(command, value)}
      className={`p-2 rounded-lg border transition-all ${
        isActive
          ? 'bg-purple-100 border-purple-300 text-purple-700'
          : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
      }`}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className={`border border-gray-300 rounded-lg overflow-hidden relative ${className}`}>
      {/* Variable Picker - Above the field as requested */}
      {enableVariables && (
        <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Variables:</span>
            <VariablePicker
              onVariableSelect={handleVariableSelect}
              clientId={clientId}
              contactId={contactId}
              buttonClassName="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-colors"
            />
          </div>
          <div className="text-xs text-gray-500">
            Insert dynamic values like {`{{tenant.company_name}}`}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 p-3 flex items-center space-x-2">
        <ToolbarButton
          command="bold"
          icon={Bold}
          title="Bold (Ctrl+B)"
          isActive={isActive.bold}
        />
        <ToolbarButton
          command="underline"
          icon={Underline}
          title="Underline (Ctrl+U)"
          isActive={isActive.underline}
        />
        
        <div className="w-px h-6 bg-gray-300 mx-2" />
        
        <ToolbarButton
          command="insertUnorderedList"
          icon={List}
          title="Bullet List"
          isActive={isActive.bulletList}
        />
        <ToolbarButton
          command="insertOrderedList"
          icon={ListOrdered}
          title="Numbered List"
          isActive={isActive.numberedList}
        />
        
        {enableMentions && (
          <>
            <div className="w-px h-6 bg-gray-300 mx-2" />
            <div className="flex items-center text-xs text-gray-500">
              <AtSign className="w-4 h-4 mr-1" />
              <span>to mention</span>
            </div>
          </>
        )}
        
        <div className="flex-1" />
        
        <div className="text-xs text-gray-500">
          <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Ctrl+B</kbd> Bold,{' '}
          <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Ctrl+U</kbd> Underline
          {enableMentions && (
            <>
              ,{' '}
              <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">@</kbd> Mention
            </>
          )}
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onClick={handleClick}
        className="p-4 min-h-[200px] focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
        style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />

      {/* Mention Dropdown */}
      {enableMentions && <MentionDropdown />}
      
      {/* Placeholder styling and mention styles */}
      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          pointer-events: none;
        }
        
        [contenteditable] ul {
          list-style-type: disc;
          margin-left: 20px;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        
        [contenteditable] ol {
          list-style-type: decimal;
          margin-left: 20px;
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        
        [contenteditable] li {
          margin-bottom: 0.25em;
        }
        
        [contenteditable] b,
        [contenteditable] strong {
          font-weight: bold;
        }
        
        [contenteditable] u {
          text-decoration: underline;
        }
        
        [contenteditable] p {
          margin-bottom: 0.5em;
        }
        
        [contenteditable] p:last-child {
          margin-bottom: 0;
        }

        /* Mention styles */
        [contenteditable] [data-mention-id] {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.5rem;
          border-radius: 9999px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        
        [contenteditable] [data-mention-id]:hover {
          opacity: 0.8;
        }

        /* Variable styles */
        [contenteditable] .inline-block {
          user-select: none;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;